import type ImageType from "@travily/types/shared/Image";
import type { AvatarColor } from "@ui/components/Avatar";
import { SelectableAvatar } from "@ui/components/SelectableAvatar";
import { tv } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import { ToggleButton, type ToggleButtonProps } from "@ui/primitives/ToggleButton";

const familyToggleItem = tv({
  base: "flex w-full max-w-[76px] cursor-pointer flex-col items-center justify-self-center gap-2 bg-transparent p-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-lg",
});

const familyToggleItemLabel = tv({
  // One line, whatever the name: a wrapped label made its tile taller than the
  // rest of the row and dragged the guest divider out of alignment. The full
  // name stays available as the tile's accessible name and its tooltip.
  base: "line-clamp-1 w-full break-all text-center text-sm leading-5 text-neutral-900 md:text-base md:leading-6",
});

/** A toggleable family member avatar with label. Use inside FamilyToggleGroup for multi-select family member picking. */
export interface FamilyToggleItemProps extends Omit<ToggleButtonProps, "children"> {
  /** Single character displayed in the avatar when there is no photo. */
  initial: string;
  /** Display name shown below the avatar. */
  label: string;
  /** Optional member photo; falls back to the colored initial. */
  avatar?: ImageType | null;
  /** Avatar background colour for the initial fallback. */
  color?: AvatarColor;
}

export function FamilyToggleItem({ initial, label, avatar, color, ...props }: FamilyToggleItemProps) {
  return (
    <ToggleButton
      aria-label={label}
      {...props}
      className={composeRenderProps(props.className, (className) => familyToggleItem({ className }))}
    >
      {({ isSelected }) => (
        <>
          <SelectableAvatar
            avatar={avatar}
            className="size-[60px]"
            color={color}
            initial={initial}
            isSelected={isSelected}
          />
          <span className={familyToggleItemLabel()} title={label}>
            {label}
          </span>
        </>
      )}
    </ToggleButton>
  );
}
